<template>
  <div class="m-layout">

    <!-- ── 頂部 Header ──────────────────────────────────────── -->
    <header class="m-header">
      <div class="m-header__left">
        <!-- 子頁（hideNav: true）顯示返回按鈕；主頁顯示 Logo -->
        <el-button
          v-if="isSubPage"
          type="text"
          class="m-back-btn"
          @click="$router.back()"
        >
          <i class="el-icon-arrow-left"></i> 返回
        </el-button>
        <img v-else src="@/assets/logo.png" class="m-logo" alt="logo" />
      </div>

      <div class="m-header__title">{{ pageTitle }}</div>

      <div class="m-header__right">
        <span class="m-username">{{ userName }}</span>
        <el-button
          type="text"
          class="m-logout-btn"
          title="登出"
          @click="handleLogout"
        >
          <i class="el-icon-switch-button"></i>
        </el-button>
      </div>
    </header>

    <!-- ── 主要內容區（TabBar 永遠顯示，因此固定保留底部空間） ── -->
    <main class="m-content has-tabbar">
      <router-view></router-view>
    </main>

    <!-- ── 底部 TabBar（在 MobileHome 內永遠顯示）─────────── -->
    <nav class="m-tabbar">
      <div
        v-for="tab in visibleTabs"
        :key="tab.path"
        class="m-tabbar__item"
        :class="{ 'm-tabbar__item--active': isActiveTab(tab) }"
        @click="goTab(tab.path)"
      >
        <i :class="tab.icon"></i>
        <span>{{ tab.label }}</span>
      </div>
    </nav>

  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'MobileHome',

  data() {
    return {
      // 底部 5 個 Tab 設定，順序對應 mobile.js 的 tabIndex
      tabs: [
        {
          path: '/m/ctm',
          label: '標籤搜尋',
          icon: 'el-icon-price-tag',
          group: '客戶智能搜尋',
        },
        {
          path: '/m/map',
          label: '地圖搜尋',
          icon: 'el-icon-location',
          group: '客戶智能搜尋',
        },
        {
          path: '/m/cust',
          label: '客戶名單',
          icon: 'el-icon-user',
          group: '客戶名單管理',
        },
        {
          path: '/m/case',
          label: '案件管理',
          icon: 'el-icon-s-order',
          group: '客戶名單管理',
        },
        {
          path: '/m/cal',
          label: '行事曆',
          icon: 'el-icon-date',
          group: '分行及標籤管理',
        },
        {
          path: '/m/combrief',
          label: '公司概況',
          icon: 'el-icon-office-building',
          group: '分行及標籤管理',
        },
      ],
    };
  },

  computed: {
    ...mapState(['un', 'userInfo']),

    currentRole() {
      const stateRole = Array.isArray(this.userInfo?.Role)
        ? this.userInfo.Role[0]
        : (this.userInfo?.RoleName || this.userInfo?.Role || '');

      if (stateRole) {
        return String(stateRole).toUpperCase();
      }

      try {
        const storedInfo = window.localStorage.getItem('userInfo');
        const parsed = storedInfo ? JSON.parse(storedInfo) : null;
        const localRole = Array.isArray(parsed?.Role)
          ? parsed.Role[0]
          : (parsed?.RoleName || parsed?.Role || '');
        return String(localRole || '').toUpperCase();
      } catch (error) {
        return '';
      }
    },

    /**
     * 依 Role + DeptID 正面表列該角色可使用的路徑白名單。
     * 不在清單內的路徑一律視為無權限。
     */
    allowedMobilePaths() {
      const role   = this.currentRole;
      const deptID = String(this.$store.getters.deptID || '').toUpperCase();

      const ALL              = ['/m/ctm', '/m/map', '/m/cust', '/m/case', '/m/cal', '/m/combrief'];
      const CTM_MAP_CUST_BRIEF = ['/m/ctm', '/m/map', '/m/cust', '/m/combrief'];
      const CTM_MAP_BRIEF    = ['/m/ctm', '/m/map', '/m/combrief'];
      const CTM_MAP          = ['/m/ctm', '/m/map'];

      // HD / HC：依所屬部門決定可用路徑
      if (['HD', 'HC'].includes(role)) {
        if (deptID === 'H77')                   return ALL;
        if (['041', 'H07'].includes(deptID))    return CTM_MAP_CUST_BRIEF;
        return []; // HD/HC 但部門未列冊 → 不顯示任何 Tab
      }

      // HC1 / HE / UC / UAO：全部六頁
      if (['HC1', 'HE', 'UC', 'UAO'].includes(role)) return ALL;

      // DO：只能用標籤搜尋與地圖搜尋
      if (role === 'DO') return CTM_MAP;

      // APA / AP：標籤搜尋、地圖搜尋、公司概況
      if (['APA', 'AP'].includes(role)) return CTM_MAP_BRIEF;

      // 未知角色 → 不顯示任何 Tab
      return [];
    },

    visibleTabs() {
      return this.tabs.filter((tab) => this.allowedMobilePaths.includes(tab.path));
    },

    /** 是否為子頁（hideNav: true 的路由，顯示返回按鈕） */
    isSubPage() {
      return !!this.$route.meta.hideNav;
    },

    /** Header 中間標題 */
    pageTitle() {
      return this.$route.meta.title || '';
    },

    /** 右上角顯示的使用者名稱 */
    userName() {
      return this.un || window.localStorage.getItem('uName') || '';
    },
  },

  methods: {
    isAllowedPath(path) {
      return this.allowedMobilePaths.includes(path);
    },

    ensureAllowedRoute() {
      if (this.isAllowedPath(this.$route.path)) {
        return;
      }

      const fallbackTab = this.visibleTabs[0]?.path;
      if (fallbackTab && this.$route.path !== fallbackTab) {
        this.$router.replace(fallbackTab);
      }
    },

    /**
     * 判斷某個 tab 是否為 active。
     * 子頁時，依 customBreadcrumbs 回推上層 tab 是否應該 highlight。
     */
    isActiveTab(tab) {
      const path = this.$route.path;

      // 完全路徑吻合
      if (path === tab.path) return true;

      // 子頁：依 group 來判斷哪個 tab 應 highlight
      if (this.isSubPage && this.$route.meta.group) {
        return tab.group === this.$route.meta.group;
      }

      return false;
    },

    /** 切換到指定 tab 路徑 */
    goTab(path) {
      if (!this.isAllowedPath(path)) {
        return;
      }

      if (this.$route.path !== path) {
        this.$router.push(path);
      }
    },

    /** 登出：複用 Vuex Logout action */
    handleLogout() {
      this.$confirm('確定要登出系統？', '登出確認', {
        confirmButtonText: '確定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          this.$store.dispatch('Logout').then(() => {
            window.localStorage.removeItem('userID');
            this.$router.push('/BIDSHome');
          });
        })
        .catch(() => {});
    },
  },

  watch: {
    '$route.path'() {
      this.ensureAllowedRoute();
    },
    currentRole() {
      this.ensureAllowedRoute();
    },
  },

  mounted() {
    this.ensureAllowedRoute();
  },
};
</script>

<style lang="scss" scoped>
// ── 版面常數 ────────────────────────────────────────────────
$header-h: 56px;
$tabbar-h: 64px;
$primary:  #1c2d5e;   // 台企銀 navy
$active:   #2a5caa;   // 淡藍 highlight
$txt-mute: #888;

// ── 根容器：撐滿 el-main ────────────────────────────────────
.m-layout {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  background: #f4f6fb;
  position: relative;
  overflow: hidden;
}

// ── 頂部 Header ─────────────────────────────────────────────
.m-header {
  flex-shrink: 0;
  height: $header-h;
  background: $primary;
  color: #fff;
  display: flex;
  align-items: center;
  padding: 0 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  z-index: 10;

  &__left {
    width: 90px;
    display: flex;
    align-items: center;
  }

  &__title {
    flex: 1;
    text-align: center;
    font-size: 17px;
    font-weight: 600;
    letter-spacing: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__right {
    width: 90px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
  }
}

.m-logo {
  height: 32px;
  width: auto;
}

.m-username {
  font-size: 13px;
  max-width: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.m-back-btn,
.m-logout-btn {
  color: #fff !important;
  font-size: 15px;
  padding: 0;

  i {
    font-size: 18px;
  }
}

.m-back-btn {
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 2px;
}

// ── 主要內容區 ───────────────────────────────────────────────
.m-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;

  // TabBar 存在時，底部留出 TabBar 高度，避免內容被遮住
  &.has-tabbar {
    padding-bottom: $tabbar-h;
  }
}

// ── 底部 TabBar ──────────────────────────────────────────────
.m-tabbar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: $tabbar-h;
  background: #fff;
  display: flex;
  border-top: 1px solid #e0e0e0;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.08);
  z-index: 100;

  &__item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    cursor: pointer;
    color: $txt-mute;
    font-size: 11px;
    transition: color 0.2s, background 0.2s;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    padding: 6px 0;

    i {
      font-size: 22px;
    }

    span {
      font-size: 11px;
      line-height: 1;
    }

    &--active {
      color: $active;

      i {
        transform: scale(1.1);
      }
    }

    &:active {
      background: #f0f4ff;
    }
  }
}

// ── 響應式：小螢幕微調（手機模式，寬 < 768px） ─────────────
@media (max-width: 767px) {
  .m-tabbar {
    height: 56px;

    &__item {
      i {
        font-size: 20px;
      }

      span {
        font-size: 10px;
      }
    }
  }
}
</style>
